import { Component, ComponentRef, EventEmitter, Input, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import moment from 'moment';
import { DmpServiceAbstract } from 'src/app/services/dmp/dmp-service.abstract';
import { DocumentService } from 'src/app/services/document/document.service';
import { EnumLogTableauDeBordFrontType, LogTableauDeBordService } from 'src/app/services/log-tableau-de-bord/log-tableau-de-bord.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { StringUtilsService } from 'src/app/services/string-utils/string-utils.service';
import { DmpFormatEnum } from 'src/app/utils/enums/dmp-format.enum';
import { DmpHlGetInteropCodesEnum } from 'src/app/utils/enums/dmp-hl-get-interop-codes-params.enum';
import { DmpVisibilityEnum } from 'src/app/utils/enums/dmp-visibility.enum';
import { DmpHlGetDirectAuthentificationDmpStatus } from 'src/app/utils/interfaces/new/dmp-hl-get-direct-authentification-dmp-status.interface';
import { DmpHlsendDocumentsDocument } from 'src/app/utils/interfaces/new/dmp-hl-send-documents-params.interface';
import { DocumentMetadata } from 'src/app/utils/interfaces/new/DocumentMetadata';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { DocumentFormComponent, DocumentFormData, ValidOperation } from '../../modal/document-form/document-form.component';
import { Identity } from 'src/app/utils/interfaces/new/lecture-ins.interface';
import { UserInfos } from 'src/app/services/dmp/dmp.service';
import { DmpGetMyDocumentsResult } from 'src/app/utils/interfaces/new/dmp-get-my-documents-result.interface';
import { PatientService } from 'src/app/services/patient/patient.service';
import { SortEnum } from 'src/app/utils/enums/sort-enum';

const DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';
const DMP_DOC_TITLE_MAX_LENGTH = 128;
const DMP_DOC_DESCRIPTION_MAX_LENGTH = 257;

interface DocumentToAddTableDisplay {
  title: string;
  weight: string;
  visibility: string;
  format: string;
  formatCode: string;
  formatCodeDisplay: string;
  category: string;
  categoryDisplay: string;
  commentaire: string;
  meta: FileMeta;
  id: string;
  author?: Array<UserInfos>;
  performer?: UserInfos;
  authenticator?: Array<UserInfos>;
}

interface FileMeta {
  file?: File;
  document?: DocumentMetadata;
}

interface PatientDocumentTableDisplay {
  title: string;
  addedAt: string;
  author: string;
  weight: string;
  meta: DocumentMetadata;
}

interface NgSelectValue {
  id: string;
  libelle: string;
}

@Component({
  selector: 'app-add-documents',
  templateUrl: './add-documents.component.html',
  styleUrls: ['./add-documents.component.scss']
})
export class AddDocumentsComponent implements OnInit {

  @Input() public dmp: DmpHlGetDirectAuthentificationDmpStatus;
  @Input() public patientId: string;
  @Input() public patientInsIdentity: Identity;
  @Input() public isBrisDeGlace: boolean;
  @Output() public endAddDocuments = new EventEmitter();

  public documentsToAddTable: Paginated<DocumentToAddTableDisplay>;
  public documentsToAddTableColumnTable = [
    { title: 'Titre', data: 'title' },
    { title: 'Poids (octets)', data: 'weight' },
    { title: 'Visibilité', data: 'visibility' },
    { title: 'Format', data: 'format' },
    { title: 'Format code', data: 'formatCodeDisplay' },
    { title: 'Catégorie', data: 'categoryDisplay' },
    { title: 'Commentaire', data: 'commentaire' },
    { title: 'Retirer', data: null },
    { title: 'Modifier', data: null },
  ];
  public isEdit = false;

  public patientDocumentsTable: Paginated<PatientDocumentTableDisplay>;
  public patientDocumentsTableColumnTable = [
    { title: 'Titre', data: 'title' },
    { title: 'Poids', data: 'weight' },
    { title: 'Ajouté le', data: 'addedAt' },
    { title: 'Auteur', data: 'author' },
    { title: 'Ajouter au DMP', data: null },
  ];
  public formGroup: FormGroup;
  private _dmpParamVisibility: Array<NgSelectValue> = [];
  private _dmpParamCategory: Array<NgSelectValue> = [];
  private _dmpParamFormat: Array<NgSelectValue> = [];

  public currentSortBy?: string = 'createdAt';
  public currentSortOrder?: SortEnum = SortEnum.DESC;

  constructor(
    private _modalSrv: ModalService,
    private _viewContainerRef: ViewContainerRef,
    private _dmpSrv: DmpServiceAbstract,
    private _formBuilder: FormBuilder,
    private _documentSrv: DocumentService,
    private _strUtilsSrv: StringUtilsService,
    private _logsTableauDeBordSrv: LogTableauDeBordService,
    private _patientSrv: PatientService,
  ) {
    const currentProfilStr = localStorage.getItem('currentProfil');
    if (currentProfilStr && currentProfilStr !== '') {
      const currentProfil = JSON.parse(currentProfilStr);
      this._dmpSrv.resourceId = currentProfil.id;
    }
  }

  ngOnInit(): void {
    this._initData();
  }

  public importDocument(): void {
    const body = document.getElementsByTagName('body')[0];
    const inputElem = document.createElement('input');
    inputElem.type = 'file';
    inputElem.setAttribute('multiple', 'true');
    body.appendChild(inputElem);
    inputElem.click();
    inputElem.addEventListener('change', (event: any) => {
      const files = event.target.files as FileList;
      for (let i = 0; i < files.length; i++) {
        const tmp = this._convertDocumentToListElemDocumentToAdd(undefined, files.item(i));
        this._addElemToDocToAddTableDisplay(tmp);
      }
      body.removeChild(inputElem);
    });
  }

  public async addToAddList(doc: PatientDocumentTableDisplay): Promise<void>{
    if (this.isEdit) {
      this._modalSrv.openAlert(
        this._viewContainerRef,
        'Action impossible',
        'Vous ne pouvez pas ajouter de document à la liste des documents à ajouter au DMP en mode édition'
      );
      return;
    }

    // Ajouter le fichier au tableau de la liste des documents à ajouter
    const tmp = this._convertDocumentToListElemDocumentToAdd(doc.meta);
    this._addElemToDocToAddTableDisplay(tmp);

    // Retirer le fichier de la liste des documents Hypermed du patient
    this.patientDocumentsTable.objects.splice(
      this.patientDocumentsTable.objects.findIndex(d => d.meta.id === doc.meta.id),
      1
    );
  }

  public async onPatientDocsPageChange(page: number): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);
    try {
      const patientDocuments = await this._documentSrv.searchDocuments(this.patientId, undefined, undefined, undefined, { 
        currentPage: page, 
        perpage: this.patientDocumentsTable.perpage || 10,
        sortBy: this.currentSortBy,
        sortOrder: this.currentSortOrder
      });
      await this._initTablePatientDocumentsDisplay(patientDocuments);
    } catch (e) {
      this._promptError(e.message);
    } finally {
      loading.destroy();
    }
  }

  public async onPatientDocsSort(event: { sortOrder: SortEnum, sortBy: string }): Promise<void> {
    this.currentSortOrder = event.sortOrder;
    this.currentSortBy = event.sortBy === 'addedAt' ? 'createdAt' : (event.sortBy === 'author' ? 'auteurCreation' : event.sortBy);
    
    await this.onPatientDocsPageChange(1);
  }

  public async removeFromAddList(doc: DocumentToAddTableDisplay): Promise<void>{
    // Ajouter le fichier au tableau de la liste des documents à ajouter
    if (doc.meta.document) {
      const tmp = this._convertDocumentToListElemPatientDocument(doc.meta.document);
      this._addElemToPatientDocumentsTableDisplay(tmp);
    }

    // Retirer le fichier de la liste des documents Hypermed du patient
    this.documentsToAddTable.objects.splice(
      this.documentsToAddTable.objects.findIndex(d => d.id === doc.id),
      1
    );
  }

  public updateFilesInfos(event: any): void {
    if (this.documentsToAddTable && this.documentsToAddTable.objects && this.documentsToAddTable.objects.length > 0) {
      for (const d of this.documentsToAddTable.objects) {
        const row = event.find(r => r.id === d.id);
        if (row) {
          d.title = row.title;
          d.category = row.category;
          d.formatCode = row.formatCode,
          d.format = row.format;
          d.visibility = row.visibility;
          d.commentaire = row.commentaire;
        }
      }

      this.isEdit = !this.isEdit;
    }
  }

  public openDocFormEdit(document: DocumentToAddTableDisplay): void {
    const modal = this._modalSrv.openModal<DocumentFormComponent>
        (this._viewContainerRef, DocumentFormComponent) as ComponentRef<DocumentFormComponent>;

    modal.instance.document = {
      category: document.category,
      formatCode: document.formatCode,
      contentInBase64: undefined,
      description: document.commentaire,
      eventCodes: undefined,
      format: DmpFormatEnum[document.format],
      title: document.title,
      visibility: DmpVisibilityEnum[document.visibility]
    };
    modal.instance.dmpStatus = this.dmp;
    modal.instance.isAuthor = true;
    modal.instance.patientInsIdentity = this.patientInsIdentity;
    modal.instance.patientId = this.patientId;

    const sub = modal.instance.closeEmitter.subscribe((editedDoc?: {docFormData: DocumentFormData, valideMessage: ValidOperation}) => {
      if (editedDoc && editedDoc.docFormData) {
        document.category = editedDoc.docFormData.category;
        document.categoryDisplay = this._dmpParamCategory.find((p) => p.id === editedDoc.docFormData.category).libelle;
        document.formatCode = editedDoc.docFormData.formatCode;
        document.formatCodeDisplay = editedDoc.docFormData.formatCode
            ? this._dmpParamFormat.find((p) => p.id === editedDoc.docFormData.formatCode).libelle : undefined;
        document.commentaire = editedDoc.docFormData.description;
        document.format = DmpFormatEnum[editedDoc.docFormData.format];
        document.title = editedDoc.docFormData.title;
        document.visibility = DmpVisibilityEnum[editedDoc.docFormData.visibility];
        document.author = editedDoc.docFormData.author;
        document.performer = editedDoc.docFormData.performer;
        document.authenticator = editedDoc.docFormData.authenticator;
      }
      modal.destroy();
      sub.unsubscribe();
    });
  }

  public async addDocumentsToDMP(): Promise<void>{
    const convertHypermedDocToDmpDoc = (doc: DocumentMetadata): Promise<string> => {
      return new Promise<string>(async (resolve, reject) => {
        try {
          const dlDoc = await this._documentSrv.downloadDocument(doc.id);
          resolve(dlDoc.payload);
        } catch (e) {
          reject(e);
        }
      });
    };

    const convertDocToDmpDoc = (doc: DocumentToAddTableDisplay): Promise<DmpHlsendDocumentsDocument> => {
      return new Promise<DmpHlsendDocumentsDocument>(async (resolve, reject) => {
        try {
          resolve({
            EventCodes: undefined,
            i_format: DmpFormatEnum[doc.format],
            i_visibility: (doc.visibility as unknown) as DmpVisibilityEnum,
            s_category: (doc.category as unknown) as DmpHlGetInteropCodesEnum,
            s_contentInBase64: doc.meta.document
              ? await convertHypermedDocToDmpDoc(doc.meta.document)
              : await this._documentSrv.convertFileToBase64(doc.meta.file),
            s_creationDate: doc.meta.document ? doc.meta.document.createdAt : moment(doc.meta.file.lastModified).toDate(),
            s_description: doc.commentaire,
            s_formatCode: doc.formatCode,
            s_serviceStartDate: undefined,
            s_serviceStopDate: undefined,
            s_title: doc.title
          });
        } catch (e) {
          reject(e);
        }
      });
    };

    const loading = this._modalSrv.openLoading(this._viewContainerRef);
    let isSendOk = false;

    try {
      // On vérifie que chaque document de la liste ait tout ses champs renseignés
      if (this.documentsToAddTable && this.documentsToAddTable.objects && this.documentsToAddTable.objects.length > 0) {
        for (const doc of this.documentsToAddTable.objects) {
          if ((!doc.category || doc.category === '')
            || (!doc.title || doc.title === '')
            || (!doc.visibility || doc.visibility === '')) {
            this._modalSrv.openAlert(
              this._viewContainerRef,
              'Données manquantes',
              'Les champs Titre, Visibilité et Catégorie sont obligatoires pour tous les documents, vous pouvez les renseigner en cliquant sur le bouton "Modifier"'
            );
            return;
          }

          if ((doc.title as string).length > DMP_DOC_TITLE_MAX_LENGTH) {
            this._modalSrv.openAlert(
              this._viewContainerRef,
              'Données erronées',
              `Les titres des documents ne doivent pas excéder ${DMP_DOC_TITLE_MAX_LENGTH} caractères`
            );
            return;
          }

          if (doc.commentaire && (doc.commentaire as string).length > DMP_DOC_DESCRIPTION_MAX_LENGTH) {
            this._modalSrv.openAlert(
              this._viewContainerRef,
              'Données erronées',
              `Les commentaires des documents ne doivent pas excéder ${DMP_DOC_DESCRIPTION_MAX_LENGTH} caractères`
            );
            return;
          }
        }

        let patient = await this._patientSrv.findOnePatientFiche(this.patientId);
        if (!patient) {
          this._promptError('Impossible de récupérer les informations du patient pour l\'envoi de documents sur le DMP.');
          return;
        }

        // On ajoute chaque document au DMP du patient
        if (this.documentsToAddTable.objects.length === 1) {
          await this._dmpSrv.hl_senddocument(
            this._viewContainerRef, {
              s_healthcareSetting: '',
              s_ins: this.dmp.ExistingTestAnswer.AdminData.Ins,
              s_birthPlaceCode: patient.cpNaissance,
              Identity: this.patientInsIdentity,
              Author: this.documentsToAddTable.objects[0].author,
              Performer: this.documentsToAddTable.objects[0].performer,
              Authenticator: this.documentsToAddTable.objects[0].authenticator,
            },
            await convertDocToDmpDoc(this.documentsToAddTable.objects[0]),
            this.documentsToAddTable.objects[0].id,
            this.patientId
          );
          isSendOk = true;
        } else {
          const documents: Array<DmpHlsendDocumentsDocument> = [];
          const documentsIds: Array<string> = [];
          for (const d of this.documentsToAddTable.objects) {
            documents.push(await convertDocToDmpDoc(d));
            documentsIds.push(d.id);
          }
          await this._dmpSrv.hl_senddocuments(
            this._viewContainerRef, {
              s_healthcareSetting: '',
              s_ins: this.dmp.ExistingTestAnswer.AdminData.Ins,
              s_birthPlaceCode: patient.cpNaissance,
              s_submissionSetTitle: this.formGroup.get('lotTitle').value,
              s_submissionSetDescription: this.formGroup.get('lotDescription').value
            },
            documents,
            documentsIds,
            this.patientId
          );
          isSendOk = true;
        }
        if (this.documentsToAddTable.objects && this.documentsToAddTable.objects.length > 0) {
          for (const doc of this.documentsToAddTable.objects) {
            const cat = doc.category || 'Document non spécifié';
            const typeDoc = cat.indexOf('++') > -1 ? cat.split('++')[0] : cat; // on tronque la string pour garder le code LOINC du typeDocument
            await this._logsTableauDeBordSrv.addLogTableauDeBord({
              patientID: this.patientId,
              typeLogs: EnumLogTableauDeBordFrontType.DMP,
              typeDocument: typeDoc,
              isSuccessful: true
            });
          }
        }
      }
    } catch (e) {
      if (this.documentsToAddTable && this.documentsToAddTable.objects && this.documentsToAddTable.objects.length > 0) {
        for (const doc of this.documentsToAddTable.objects) {
          const cat = doc.category || 'Document non spécifié';
          const typeDocEchec = cat.indexOf('++') > -1 ? cat.split('++')[0] : cat;
          await this._logsTableauDeBordSrv.addLogTableauDeBord({
            patientID: this.patientId,
            typeLogs: EnumLogTableauDeBordFrontType.DMP,
            typeDocument: typeDocEchec,
            isSuccessful: false
          });
        }
      }
      if (e.message === 'Operation aborted: invalid healthcare setting.'
        || (e.message as string).includes('CLASSCODE')) {
        this._modalSrv.openAlert(
          this._viewContainerRef,
          'Erreur',
          'Le cadre de soins lié à cette carte ne permet pas d\'ajouter de document avec la catégorie spécifiée'
        );
      } else {
        this._promptError(e.message);
      }
    } finally {
      loading.destroy();

      if (isSendOk) {
        console.log('OK ALERT');
        const compRef = this._modalSrv.openAlert(
          this._viewContainerRef,
          'Envoi',
          'Document(s) envoyé(s) avec succès'
        );

        const sub = compRef.instance.closeEmitter.subscribe(_ => {
          this.endAddDocuments.emit();
        })
      }
    }
  }

  public cancelAdd(): void {
    if (this.documentsToAddTable && this.documentsToAddTable.objects && this.documentsToAddTable.objects.length > 0) {
      const modal = this._modalSrv.openModalConfirmation(
        this._viewContainerRef,
        'Confirmation',
        'Si vous annulez, les documents ne seront pas ajoutés au DMP.\n\rVoulez-vous annuler ?'
      );

      const sub = modal.instance.closeEmitter.subscribe((res) => {
        if (res) {
          this.endAddDocuments.emit();
        }
        sub.unsubscribe();
      });
    } else {
      this.endAddDocuments.emit();
    }
  }

  private async _initData(): Promise<void>{
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      // On récupère les documents Hypermed de l'utilisateur par défaut triés du plus récent au plus ancien
      const patientDocuments = await this._documentSrv.searchDocuments(this.patientId, undefined, undefined, undefined, {
        currentPage: 1,
        perpage: 10,
        sortBy: 'createdAt',
        sortOrder: SortEnum.ASC
      });

      const interopCodes = await this._dmpSrv.hl_getInteropCodes(this._viewContainerRef, [DmpHlGetInteropCodesEnum.docTypes, DmpHlGetInteropCodesEnum.docFormats, DmpHlGetInteropCodesEnum.healthcareSettings]);

      if (interopCodes) {
        if (interopCodes[DmpHlGetInteropCodesEnum.docFormats]) {
          for (const docFormat of interopCodes[DmpHlGetInteropCodesEnum.docFormats]) {
            this._dmpParamFormat.push({
              id: docFormat.s_code,
              libelle: docFormat.s_displayName
            });
          }
        }

        // On récupère les catégories de DMP
        if (interopCodes[DmpHlGetInteropCodesEnum.docTypes]) {
          for (const docType of interopCodes[DmpHlGetInteropCodesEnum.docTypes]) {
            this._dmpParamCategory.push({
              id: docType.s_code,
              libelle: docType.s_displayName
            });
          }
        }
      }

      // On récupère les visibilités de DMP
      Object.keys(DmpVisibilityEnum).filter((v) => {
        if (isNaN(Number(v))) {
          this._dmpParamVisibility.push({
            id: v,
            libelle: v
          });
        }
      });

      // Une fois les données récupérées, on initialise le tableau (_initTablePatientDocumentsDisplay())
      await this._initTablePatientDocumentsDisplay(patientDocuments);

      this.formGroup = this._formBuilder.group({
        lotTitle: [''],
        lotDescription: ['']
      });
    } catch (e) {
      this._promptError(e.message);
    } finally {
      loading.destroy();
    }
  }

  private _convertDocumentToListElemDocumentToAdd = (doc?: DocumentMetadata, file?: File): DocumentToAddTableDisplay => {
    const makeFalseId = (): string => {
      let result = '';
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      const charactersLength = characters.length;
      for (let i = 0; i < 12; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
      }
      return result;
    };

    if (doc) {
      return {
        category: '',
        categoryDisplay: '',
        formatCode: '',
        formatCodeDisplay: '',
        commentaire: '',
        format: '',
        meta: {
          document: doc
        },
        title: doc.titre,
        visibility: '',
        weight: doc.weight.toString(),
        id: doc.id
      };
    } else if (file) {
      return {
        category: '',
        categoryDisplay: '',
        formatCode: '',
        formatCodeDisplay: '',
        commentaire: '',
        format: '',
        meta: {
          file
        },
        title: file.name,
        visibility: '',
        weight: file.size.toString(),
        id: makeFalseId()
      };
    }
  }

  private _addElemToDocToAddTableDisplay(document: DocumentToAddTableDisplay): void {
    if (this.documentsToAddTable) {
      this.documentsToAddTable.objects.push(document);
      this.documentsToAddTable.page = 0;
      this.documentsToAddTable.perpage = 0;
      this.documentsToAddTable.total = 0;
    } else {
      this.documentsToAddTable = {
        objects: [document],
        page: 0,
        perpage: 0,
        total: 0
      };
    }
  }

  private _convertDocumentToListElemPatientDocument(doc: DocumentMetadata): PatientDocumentTableDisplay {
    return {
      addedAt: moment(doc.createdAt).format(DATE_DISPLAY_FORMAT),
      author: `${doc.auteurCreation.qualite} ${doc.auteurCreation.nom.toLocaleUpperCase()} ${this._strUtilsSrv.uppercaseFirstCharString(doc.auteurCreation.prenom)}`,
      meta: doc,
      title: doc.titre,
      weight: doc.weight.toString()
    };
  }

  private _addElemToPatientDocumentsTableDisplay(document: PatientDocumentTableDisplay): void {
    if (this.patientDocumentsTable) {
      this.patientDocumentsTable.objects.push(document);
      this.patientDocumentsTable.page = 0;
      this.patientDocumentsTable.perpage = 0;
      this.patientDocumentsTable.total = 0;
    } else {
      this.patientDocumentsTable = {
        objects: [document],
        page: 0,
        perpage: 0,
        total: 0
      };
    }
  }

  private async _initTablePatientDocumentsDisplay(documentsFound: Paginated<DocumentMetadata>): Promise<void> {

    // On réinitialise le tableau d'affichage
    if (this.patientDocumentsTable) {
      this.patientDocumentsTable.objects = [];
    }

    // Rechercher si un document DMP est associé à un document patient
    let documentsDMP:Array<{ documentDmp: DmpGetMyDocumentsResult; documentId: string; }> = []
    if (documentsFound && documentsFound.objects && documentsFound.objects.length > 0) {
      documentsDMP = await this._dmpSrv.searchDMPDocumentByDocumentId(documentsFound.objects.map(d => d.id));
    }
    const documentsTmp: Array<PatientDocumentTableDisplay> = [];
    if (documentsFound && documentsFound.objects && documentsFound.objects.length > 0) {
      for (const df of documentsFound.objects) {
        // Si un documentDMP existe on l'affiche pas dans la liste des documents
        if (documentsDMP && documentsDMP.length > 0 && documentsDMP.find(d => d.documentId=== df.id)) {
          continue;
        }
        documentsTmp.push(this._convertDocumentToListElemPatientDocument(df));
      }

      if (this.patientDocumentsTable) {
        this.patientDocumentsTable.objects = [...documentsTmp];
        this.patientDocumentsTable.page = documentsFound.page;
        this.patientDocumentsTable.perpage = documentsFound.perpage;
        this.patientDocumentsTable.total = documentsFound.total;
      } else {
        this.patientDocumentsTable = {
          objects: documentsTmp,
          page: documentsFound.page,
          perpage: documentsFound.perpage,
          total: documentsFound.total
        };
      }
    }
  }

  private _promptError(msg: string): void {
    console.log('_promptError - msg : ', msg);
    let contextMsg = 'Une erreur est survenue';

    const msgSplit = msg.split('\n');
    if (msgSplit.length > 1) {
      const obj = {};
      for (const str of msgSplit) {
        const strSplit = str.split(/ : (.*)/s);
        obj[strSplit[0]] = Number.isNaN(Number(strSplit[1])) ? strSplit[1] : Number(strSplit[1]);
      }
      if (typeof obj === 'object') {
        if ((obj as any).s_apiErrorTypeDescription === 'DMPError') {
          contextMsg = `${(obj as any).s_apiErrorDescription}`;
          contextMsg += `\n${(obj as any).s_apiErrorDmpErrorDetails}`;

          msg = `Code d'erreur : ${(obj as any).s_apiErrorDmpErrorCode}`;
          msg += `\nType de l'erreur : ${(obj as any).s_apiErrorTypeDescription}`;
          msg += `\nContexte de l'erreur : ${(obj as any).s_apiErrorContext}`;
          msg += `\nDétails de l'erreur : ${(obj as any).s_apiErrorExtendedInformations}`;
        } else if ((obj as any).s_apiErrorTypeDescription === 'ApiError') {
          contextMsg = `Code d'erreur : ${(obj as any).i_apiErrorCode}`;
          contextMsg += `\nType de l'erreur : ${(obj as any).s_apiErrorTypeDescription}`;
          contextMsg += `\nContexte de l'erreur : ${(obj as any).s_apiErrorContext}`;
          contextMsg += `\nDescription de l'erreur : ${(obj as any).s_apiErrorDescription}`;
          contextMsg += `\nDétails de l'erreur : ${(obj as any).s_apiErrorExtendedInformations}`;
          msg = undefined;
        } else if ((obj as any).s_status && ((obj as any).s_status === 'ERROR' || (obj as any).s_status === 'API_ERROR')) {
          contextMsg = (obj as any).s_apiErrorDescription ? (obj as any).s_apiErrorDescription : contextMsg;
          if ((obj as any).s_apiErrorDmpErrorCode && (obj as any).s_apiErrorTypeDescription && (obj as any).s_apiErrorDmpErrorDetails) {
            msg = `Code d'erreur : ${(obj as any).s_apiErrorDmpErrorCode}`;
            msg += `\nType de l'erreur : ${(obj as any).s_apiErrorTypeDescription}`;
            msg += `\nContexte de l'erreur : ${(obj as any).s_apiErrorContext}`;
            msg += `\nDétails de l'erreur : ${(obj as any).s_apiErrorDmpErrorDetails}`;
          } else {
            msg = (obj as any).s_apiErrorDmpErrorDetails ? (obj as any).s_apiErrorDmpErrorDetails : msg;
          }
        }

        if ((obj as any).s_requestFrameInBase64) {
          console.error('Message d\'erreur pour le support (request) : ', (obj as any).s_requestFrameInBase64);
        }
        if ((obj as any).s_answerFrameInBase64) {
          console.error('Message d\'erreur pour le support (response) : ', (obj as any).s_answerFrameInBase64);
        }
      }
    }

    this._modalSrv.openAlert(
      this._viewContainerRef,
      'Erreur',
      contextMsg,
      undefined,
      msg
    );
  }

}
